/**
 * "Safe to Miss?" Quick Answer Panel (Phase 3 — Step 3.5).
 */

import React, { useState, useMemo } from 'react';
import { Check, AlertTriangle, X } from 'lucide-react';
import { useAppStore } from '@/store';
import { simulateFutureAbsences } from '@abs/shared-logic';
import { computeTotalSessions } from '@abs/shared-logic';
import { computeEffectiveAbsences } from '@abs/shared-logic';
import { computePassFailStatus } from '@abs/shared-logic';
import { todayISO } from '@abs/shared-logic';
import type { Course } from '@abs/shared-types';

interface SafeToMissPanelProps {
  course: Course;
}

export function SafeToMissPanel({ course }: SafeToMissPanelProps): React.ReactElement {
  const store = useAppStore;
  const [selectedDate, setSelectedDate] = useState('');

  const result = useMemo(() => {
    if (!selectedDate) return null;

    const state = store.getState();
    const policy = state.policies.find((p) => p.id === course.policyId);
    if (!policy) return null;

    const entries = state.entries.filter((e) => e.courseId === course.id);
    const { total } = computeTotalSessions(
      course.meetingPatterns, course.sessionOverrides,
      course.startDate, course.endDate,
    );

    // Simulate 1 additional absence
    const sim = simulateFutureAbsences(course, policy, entries, total, 1, course.baseGrade);
    const currentEffective = computeEffectiveAbsences(entries, policy);

    const safe = sim.projectedRiskLevel === 'satisfactory' || sim.projectedRiskLevel === 'warning';
    const atRisk = sim.projectedRiskLevel === 'at_risk';
    const critical = sim.projectedRiskLevel === 'critical' || sim.isAutoFail;

    return {
      safe,
      atRisk,
      critical,
      projectedPercentage: sim.projectedPercentage.toFixed(1),
      projectedPercentageNum: sim.projectedPercentage,
      projectedGrade: sim.projectedGrade,
      tierTriggered: sim.tierTriggered,
      remainingBudget: course.creditHours, // placeholder
      currentRisk: currentEffective.totalPenaltyAbsences,
    };
  }, [selectedDate, course, store]);

  return (
    <div className="safe-to-miss" role="region" aria-label="Safe to Miss">
      <h4>Safe to Miss?</h4>
      <div className="safe-to-miss__picker">
        <label>
          Select a future session date
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={todayISO()}
          />
        </label>
      </div>

      {result ? (
        <div className={`safe-to-miss__answer ${result.critical ? 'safe-to-miss--danger' : result.atRisk ? 'safe-to-miss--warning' : 'safe-to-miss--safe'}`} role="alert">
          <div className="safe-to-miss__verdict">
            {result.safe ? (<><Check aria-hidden="true" /> Yes</>) : result.atRisk ? (<><AlertTriangle aria-hidden="true" /> Risky</>) : (<><X aria-hidden="true" /> No</>)}
          </div>
          <div className="safe-to-miss__explanation">
            {result.critical && 'Missing this session would put you at or past your absence limit.'}
            {result.atRisk && `Missing this session puts you at risk. Projected: ${result.projectedPercentage}% attendance.`}
            {result.safe && `You can afford to miss this session. Projected: ${result.projectedPercentage}% attendance.`}
            {result.tierTriggered && ` Penalty triggered: ${result.tierTriggered.description}.`}
            {course.isPassFail
              ? (() => {
                  const pf = computePassFailStatus(true, course.passThreshold ?? 70, result.projectedPercentageNum);
                  return pf ? ` ${pf.isPassing ? 'PASS' : 'FAIL'} (margin ${pf.margin}%).` : '';
                })()
              : (result.projectedGrade ? ` Grade: ${result.projectedGrade}.` : '')}
          </div>
        </div>
      ) : (
        <p className="safe-to-miss__hint">Select a future session date above to see whether it's safe to miss.</p>
      )}
    </div>
  );
}
